import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Target, Zap, Clock } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="landing-page">
      {/* Header */}
      <header className="px-6 md:px-12 lg:px-20 py-5 border-b border-zinc-100">
        <a
          href="https://unpaved.ai"
          target="_blank"
          rel="noopener noreferrer"
          className="font-serif text-xl tracking-tight hover:opacity-60 transition-opacity"
          data-testid="unpaved-logo"
        >
          Unpaved
        </a>
      </header>

      {/* Hero */}
      <main className="flex-1 flex items-center justify-center px-6 md:px-12 lg:px-20">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="w-full max-w-2xl mx-auto text-center py-16 sm:py-20 lg:py-0"
        >
          {/* Overline */}
          <motion.p
            variants={itemVariants}
            className="text-[11px] font-medium tracking-[0.25em] uppercase text-zinc-400 mb-10"
            data-testid="overline-text"
          >
            Free Operational Diagnostic
          </motion.p>

          {/* Main Headline */}
          <motion.h1
            variants={itemVariants}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] mb-7"
            data-testid="hero-headline"
          >
            Your AI{' '}
            <span className="italic">Org Health</span>{' '}
            Score
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            variants={itemVariants}
            className="text-base sm:text-lg leading-relaxed text-zinc-500 mb-14 max-w-lg mx-auto"
            data-testid="hero-subtitle"
          >
            Most AI transformations stall because companies don't know how work actually gets done. This 4-minute diagnostic shows you where to start.
          </motion.p>

          {/* Stats Row */}
          <motion.div
            variants={itemVariants}
            className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 mb-14"
            data-testid="stats-row"
          >
            <span className="inline-flex items-center gap-2 text-[13px] text-zinc-400">
              <Target className="w-3.5 h-3.5" strokeWidth={1.5} />
              6 Operational Dimensions
            </span>
            <span className="inline-flex items-center gap-2 text-[13px] text-zinc-400">
              <Zap className="w-3.5 h-3.5" strokeWidth={1.5} />
              20 Diagnostic Questions
            </span>
            <span className="inline-flex items-center gap-2 text-[13px] text-zinc-400">
              <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
              4 min To Your Baseline
            </span>
          </motion.div>

          {/* CTA Button */}
          <motion.div variants={itemVariants} className="mb-6">
            <button
              onClick={() => navigate('/assess')}
              data-testid="start-assessment-btn"
              className="inline-flex items-center gap-2.5 bg-zinc-950 text-white px-10 py-4 text-sm font-medium rounded-full hover:bg-zinc-800 transition-all duration-200 btn-glow"
            >
              Start Your Assessment
              <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>

          {/* Trust Signal */}
          <motion.p
            variants={itemVariants}
            className="text-xs text-zinc-400 tracking-wide"
            data-testid="trust-signal"
          >
            Used by COOs and Ops Directors at scaling organisations
          </motion.p>
        </motion.div>
      </main>

      {/* Footer accent */}
      <footer className="px-6 md:px-12 lg:px-20 py-5 border-t border-zinc-100 flex items-center justify-between">
        <span className="text-[11px] text-zinc-300 tracking-wide">unpaved.ai</span>
        <a
          href="https://unpaved.ai"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] text-zinc-400 hover:text-zinc-600 transition-colors"
          data-testid="footer-link"
        >
          Learn more about Unpaved
        </a>
      </footer>
    </div>
  );
};

export default LandingPage;
